import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

import type { PastedConversationMessage } from '@/lib/external-email/parse-pasted-email';

const EARLIER_MESSAGES_ROOT = ['pasted-email-earlier-messages'] as const;

export const pastedEmailEarlierMessagesKeys = {
  all: EARLIER_MESSAGES_ROOT,
  presence: (interactionId: string) =>
    [...EARLIER_MESSAGES_ROOT, interactionId, 'presence'] as const,
  messages: (interactionId: string) =>
    [...EARLIER_MESSAGES_ROOT, interactionId, 'messages'] as const,
};

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** The stored jsonb, mapped; anything off the documented shape is dropped. */
export function toEarlierMessages(value: unknown): PastedConversationMessage[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry: unknown) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const {
      direction,
      body,
      sender_name: senderName,
      sender_email: senderEmail,
    } = entry as Record<string, unknown>;
    if ((direction !== 'inbound' && direction !== 'outbound') || typeof body !== 'string') {
      return [];
    }
    return [
      {
        direction,
        senderName: optionalString(senderName),
        senderEmail: optionalString(senderEmail),
        body,
      },
    ];
  });
}

/**
 * Whether a pasted email came with earlier messages, without reading them:
 * the column can hold most of a pasted thread, so a card asks only this until
 * someone opens the conversation.
 */
export function usePastedEmailHasEarlierMessages(interactionId: string) {
  return useQuery({
    queryKey: pastedEmailEarlierMessagesKeys.presence(interactionId),
    // Written once at paste time and never edited by the app.
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const { data, error } = await supabase
        .from('correspondence_interactions')
        .select('id')
        .eq('id', interactionId)
        .not('earlier_messages', 'is', null)
        .abortSignal(signal)
        .maybeSingle();
      if (error) {
        throw error;
      }
      return data !== null;
    },
  });
}

/**
 * The messages before a pasted email in its conversation, oldest first. Read
 * on demand rather than carried by the timeline RPC, since only a pasted
 * conversation has any and the column can hold most of a pasted thread.
 */
export function usePastedEmailEarlierMessages(interactionId: string, enabled = true) {
  return useQuery({
    queryKey: pastedEmailEarlierMessagesKeys.messages(interactionId),
    enabled,
    // Written once at paste time and never edited by the app.
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const { data, error } = await supabase
        .from('correspondence_interactions')
        .select('earlier_messages')
        .eq('id', interactionId)
        .abortSignal(signal)
        .maybeSingle();
      if (error) {
        throw error;
      }
      return toEarlierMessages(data?.earlier_messages);
    },
  });
}
